import {
  isOrderEditable,
  WINDOW_ORDER_STATUS,
  WINDOW_ORDER_STATUS_OPTIONS,
  WindowOrder,
  WindowOrderStatus,
  WindowOrderSummary,
} from '@cutr/constants/windows/windows';
import { getOrderPricingSummary } from '@cutr/constants/windows/windows-pricing';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/backend';
import { PaginatedData } from '@/api/backend/agent';
import { BasePagination } from '@/blocks/BasePagination';
import { Multiselect } from '@/blocks/Multiselect';
import { Button, RouterButton } from '@/primitives/Button';
import { Download, Edit, Icon, View } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { useCurrentFeatures } from '@/theme';
import { currencyFormatter, dateFormatter } from '@/utils/format';
import { useDebounce } from '@/utils/hooks';

import { useWorkspaceFilterStore } from '../api/workspaceFilterStore';
import { StatusBadge } from '../blocks/StatusBadge';
import { keys } from '../queries/order/keys';
import { windowApi } from '../queries/windowApi';
import { redirectToLoginPage } from './Errors';
import styles from './ResellerWorkspace.module.css';

interface ResellerWorkspaceContextType {
  data: PaginatedData<WindowOrder>;
  setData: (data: PaginatedData<WindowOrder>) => void;
  replaceOrder: (order: WindowOrder) => void;
}

const ResellerWorkspaceContext =
  createContext<ResellerWorkspaceContextType | null>(null);

export const ResellerOrdersProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [data, setOrders] = useState<PaginatedData<WindowOrder>>({
    rows: [],
    totalPages: 0,
    totalRows: 0,
  });

  const setData = React.useCallback(
    (data: PaginatedData<WindowOrder>) => {
      setOrders(data);
    },
    [setOrders]
  );

  const replaceOrder = React.useCallback(
    (order: WindowOrder) => {
      setOrders((prev) => {
        return {
          ...prev,
          rows: prev.rows?.map((row) => (row.id === order.id ? order : row)),
        };
      });
    },
    [setOrders]
  );

  return (
    <ResellerWorkspaceContext.Provider
      value={{
        data,
        setData,
        replaceOrder,
      }}
    >
      {children}
    </ResellerWorkspaceContext.Provider>
  );
};

export const useResellerOrders = () => {
  const context = useContext(ResellerWorkspaceContext);
  if (!context) {
    throw new Error(
      'useResellerOrders must be used within an ResellerOrdersProvider'
    );
  }
  return context;
};

type Column<T> = {
  accessorKey: keyof T;
  cell?: ({ row }: { row: T }) => React.ReactNode;
  style?: React.CSSProperties;
};

export const ResellerWorkspace = () => {
  return (
    <ResellerOrdersProvider>
      <ResellerOrders />
    </ResellerOrdersProvider>
  );
};

const ResellerOrders = () => {
  const { search, statuses, dateFilters, pagination, setPagination } =
    useWorkspaceFilterStore();
  const debouncedSearch = useDebounce(search, 700, search);
  const debouncedStatuses = useDebounce(statuses, 500, statuses);

  const lastSearch = React.useRef(debouncedSearch);
  const { setData } = useResellerOrders();

  const { agentWorkspaceDisabledColumns } = useCurrentFeatures();

  const queryClient = useQueryClient();

  // @ts-ignore
  const columns: Column<WindowOrder>[] = [
    {
      accessorKey: 'title',
    },
    {
      accessorKey: 'createdAt',
      cell: ({ row }: { row: WindowOrder }) => dateFormatter(row.createdAt),
    },
    {
      accessorKey: 'shortId',
    },
    {
      accessorKey: 'status',
      cell: ({ row }: { row: WindowOrder }) => <StatusBadge order={row} />,
      style: {
        textAlign: 'center',
        maxWidth: '40px',
      },
    },
    {
      accessorKey: 'totalAmountExVat',
      cell: ({ row }: { row: WindowOrder }) => {
        const { totalAmount } = getOrderPricingSummary(row);
        return currencyFormatter(totalAmount / 100, row.currency);
      },
      style: {
        textAlign: 'center',
      },
    },
  ].filter(
    (column) => !agentWorkspaceDisabledColumns.includes(column.accessorKey)
  );

  React.useEffect(() => {
    const { createdAfter, createdBefore, submittedAfter, submittedBefore } =
      dateFilters;
    const queryParams = {
      ...pagination,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(debouncedStatuses && debouncedStatuses.length > 0
        ? { statuses: debouncedStatuses }
        : {}),
      ...(createdAfter ? { createdAfter } : {}),
      ...(createdBefore ? { createdBefore } : {}),
      ...(submittedAfter ? { submittedAfter } : {}),
      ...(submittedBefore ? { submittedBefore } : {}),
    };

    queryClient
      .fetchQuery<PaginatedData<WindowOrder>>({
        queryKey: keys.getAll(queryParams),
        queryFn: () => windowApi.getAllOrders(queryParams),
      })
      .then((data) => {
        setData(data);
        if (lastSearch.current !== debouncedSearch) {
          setPagination({
            pageSize: pagination.pageSize,
            pageIndex: 0,
          });
          lastSearch.current = debouncedSearch;
        }
      })
      .catch((error) => {
        if (error instanceof ApiError && error.response?.status === 401) {
          return redirectToLoginPage();
        }
        throw error;
      });
  }, [
    pagination,
    debouncedSearch,
    debouncedStatuses,
    dateFilters,
    queryClient,
    setPagination,
    setData,
  ]);

  return (
    <main className={styles.content}>
      <Filters />
      <div className={styles.orderSection}>
        <ResellerOrdersTable columns={columns} />
      </div>
    </main>
  );
};

type ResellerOrdersTableProps = {
  columns: Column<WindowOrder>[];
};

const ResellerOrdersTable = ({ columns }: ResellerOrdersTableProps) => {
  const { t } = useTranslation();
  const isFetching = useIsFetching({ queryKey: keys.getAllPrefix });

  const { pagination, setPagination } = useWorkspaceFilterStore();

  const { data } = useResellerOrders();

  const rows = data.rows;
  const pageMetadata = {
    ...pagination,
    totalRows: data.totalRows ?? 0,
    totalPages: data.totalPages ?? 0,
  };

  return (
    <>
      <table>
        <thead>
          <tr>
            {columns.map(({ accessorKey, style }) => (
              <th key={accessorKey} style={style}>
                {t(`reseller.columnKey.${accessorKey}`)}
              </th>
            ))}
            <th style={{ textAlign: 'center' }}>{t('reseller.actions')}</th>
          </tr>
        </thead>
        <tbody>
          <Rows rows={rows} columns={columns} />
        </tbody>
      </table>
      <div className="flexAlign opposites">
        <div style={{ width: '100%' }}>
          <BasePagination
            totalPages={pageMetadata.totalPages}
            currentPage={pageMetadata.pageIndex + 1}
            disabled={isFetching > 0}
            onPageChanged={(page) => {
              setPagination({
                pageSize: pageMetadata.pageSize,
                pageIndex: page - 1,
              });
            }}
          />
        </div>
        <div className="flexAlign">
          <span style={{ whiteSpace: 'nowrap' }}>{t('agent.show')}</span>
          {[10, 30, 50].map((pageSize) => (
            <Button
              key={pageSize}
              size="xs"
              className={pageMetadata.pageSize !== pageSize ? 'outline' : ''}
              disabled={isFetching > 0}
              onClick={() =>
                pageMetadata.pageSize !== pageSize &&
                setPagination({
                  pageSize,
                  pageIndex: 0,
                })
              }
            >
              {pageSize}
            </Button>
          ))}
        </div>
      </div>
    </>
  );
};

const Rows = ({
  rows,
  columns,
}: {
  rows?: WindowOrder[];
  columns: Column<WindowOrder>[];
}) => {
  const isFetching = useIsFetching({ queryKey: keys.getAllPrefix });

  if (rows && rows.length > 0) {
    return (
      <>
        {rows.map((row) => (
          <TableRow key={row.id} row={row} columns={columns} />
        ))}
      </>
    );
  }
  if (isFetching) {
    return (
      <>
        {Array.from({ length: 10 }).map((_, index) => (
          <SkeletonRow key={index} columns={columns} />
        ))}
      </>
    );
  }

  return <NoResultsRow columns={columns} />;
};

const TableRow = ({
  row,
  columns,
}: {
  row: WindowOrder;
  columns: Column<WindowOrder>[];
}) => {
  const navigate = useNavigate();
  const navigateTo = useNavigateToOrder(row);
  return (
    <tr
      key={row.id}
      style={{ cursor: 'pointer' }}
      onClick={() => {
        navigate(navigateTo);
      }}
    >
      {columns.map(({ accessorKey, cell, style }) => {
        return (
          <td key={`${accessorKey} + ${row.id}`} style={style}>
            {/* @ts-ignore good luck */}
            {cell ? cell({ row }) : row[accessorKey]}
          </td>
        );
      })}
      <td>
        <RowActions order={row} />
      </td>
    </tr>
  );
};

const SkeletonRow = ({ columns }: { columns: Column<WindowOrder>[] }) => {
  return (
    <tr style={{ opacity: 0.7 }}>
      {columns.map((_, index) => (
        <td key={index} className={styles.loading}>
          <div className={styles.bar}></div>
        </td>
      ))}
      <td>
        <div className={styles.actions}>
          <Button className="smaller pill snug outline" disabled={true}>
            <Icon icon={<Download />} />
          </Button>
          <Button className="smaller pill snug outline" disabled={true}>
            <Icon icon={<View />} />
          </Button>
          <Button className="smaller pill snug outline" disabled={true}>
            <Icon icon={<Edit />} />
          </Button>
        </div>
      </td>
    </tr>
  );
};

const NoResultsRow = ({ columns }: { columns: Column<WindowOrder>[] }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td
        colSpan={columns.length + 1}
        style={{ textAlign: 'center', padding: '20px' }}
      >
        {t('agent.noResultsFound')}
      </td>
    </tr>
  );
};

const Filters = () => {
  const { t } = useTranslation();
  const {
    dateFilters,
    setDateFilters,
    search,
    setSearch,
    statuses,
    setStatuses,
  } = useWorkspaceFilterStore();
  const { createdAfter, createdBefore, submittedAfter, submittedBefore } =
    dateFilters;

  // backend does not support date filters yet
  const dateFiltersActive = false;

  return (
    <div className={styles.filterSection}>
      <div className={styles.filterField}>
        <label>{t('agent.filters.search')}</label>
        <Input
          type="text"
          placeholder={t('agent.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className={styles.filterField}>
        <label>{t('agent.filters.status')}</label>
        <Multiselect
          id="status"
          value={t('agent.statuses')}
          options={WINDOW_ORDER_STATUS_OPTIONS.map((value) => ({
            label: t(`reseller.orderStatus.${value}`),
            value,
            selected: statuses.includes(value),
            component: () => <StatusBadge status={value} />,
          }))}
          onSelected={(selectedValues) => {
            setStatuses([...selectedValues]);
          }}
          className={styles.filterSelect}
        />
      </div>

      {dateFiltersActive && (
        <>
          <div className={styles.filterField}>
            <label>{t('agent.filters.createdAfter')}</label>
            <Input
              type="date"
              value={createdAfter}
              onChange={(e) =>
                setDateFilters({ ...dateFilters, createdAfter: e.target.value })
              }
            />
          </div>

          <div className={styles.filterField}>
            <label>{t('agent.filters.createdBefore')}</label>
            <Input
              type="date"
              value={createdBefore}
              onChange={(e) =>
                setDateFilters({
                  ...dateFilters,
                  createdBefore: e.target.value,
                })
              }
            />
          </div>

          <div className={styles.filterField}>
            <label>{t('agent.filters.submittedAfter')}</label>
            <Input
              type="date"
              value={submittedAfter}
              onChange={(e) =>
                setDateFilters({
                  ...dateFilters,
                  submittedAfter: e.target.value,
                })
              }
            />
          </div>

          <div className={styles.filterField}>
            <label>{t('agent.filters.submittedBefore')}</label>
            <Input
              type="date"
              value={submittedBefore}
              onChange={(e) =>
                setDateFilters({
                  ...dateFilters,
                  submittedBefore: e.target.value,
                })
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

type RowActionsProps = {
  order: WindowOrderSummary;
};

const RowActions = ({ order }: RowActionsProps) => {
  const isEditable = isOrderEditable(order.status);
  const navigateTo = useNavigateToOrder(order);
  return (
    <div className={styles.actions}>
      <RouterButton className="smaller pill snug outline" to={navigateTo}>
        <Icon icon={isEditable ? <Edit /> : <View />} />
      </RouterButton>
    </div>
  );
};

const useNavigateToOrder = (order: WindowOrderSummary) => {
  const reviewStatuses: WindowOrderStatus[] = [
    WINDOW_ORDER_STATUS.quote_requested,
    WINDOW_ORDER_STATUS.quote_ready,
    WINDOW_ORDER_STATUS.order_placed,
  ];

  if (reviewStatuses.includes(order.status)) {
    return `/windows/orders/${order.id}/review`;
  } else {
    return `/windows/orders/${order.id}`;
  }
};
